import { PlatformIcon, platformLabel } from 'components/PlatformIcon'
import { cn } from 'lib/utils'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ExternalLink,
  Loader2,
  Plus,
  PenLine,
  RotateCcw,
} from 'lucide-react'
import { useState } from 'react'
import type { LivePlatform } from '../store/idea-store'
import { getPublishFailureCopy } from '../utils/publish-failure-copy'
import { PublishFailureCopyBlock } from './post-card-chips'

/** Where the restriction alert sends the user to sort it out. */
const PLATFORM_HOME_URL: Record<string, string> = {
  instagram: 'https://www.instagram.com/',
  facebook: 'https://www.facebook.com/',
}

interface PostModalPlatformRowProps {
  platform: LivePlatform
  /** True when this row's preview is currently rendered on the right. */
  isPreviewing: boolean
  /** True when the user has added this platform to the publish list. */
  isSelected: boolean
  /** True when the platform is connected (or doesn't need OAuth). */
  isConnected: boolean
  /** True while an OAuth popup is in flight for this platform (submit-time). */
  isConnecting: boolean
  /** Make this row the previewed platform on the right column. */
  onPreview: () => void
  /** Toggle selection-for-publish. Pure selection — no OAuth side effects. */
  onToggleSelect: () => void
  /**
   * When set, the +/✓ toggle is disabled and this string is surfaced as the
   * tooltip — e.g. mixed-media carousel + Facebook. Preview row click still
   * works so the user can see what the platform would render.
   */
  disabledReason?: string
  /**
   * When true, the row's selection is fixed by the parent (composer entry
   * path) — the +/✓ badge becomes a static read-only indicator. The preview
   * row click still works. Distinct from `disabledReason`: locked rows look
   * intentional and selected, not "blocked".
   */
  locked?: boolean
  /** True when this platform carries user-customized copy (pinned variant). */
  isCustomized?: boolean
  /**
   * Publish failed for this platform — surfaces the destructive AlertTriangle
   * indicator in the trailing slot with a popover explaining what happened
   * and where to fix it. `reason` is the machine token from the server
   * classifier (`token_expired`, `account_restricted`, `image_error`, …);
   * copy comes from `getPublishFailureCopy` so raw tokens never leak.
   *
   * `'account_restricted'` is the composition rule from the plan: the
   * platform's provider-level restriction (Meta code 25) rides through this
   * same prop, and the popover render below keeps its bespoke external unlock
   * links pixel-identical to the old `isRestricted` treatment so there is
   * exactly ONE indicator per row — never two competing alerts.
   */
  failure?: { reason: string }
  /**
   * Per-platform recovery. When provided (marker-based failure on an
   * unpublished creative — NOT a provider restriction), the failure dropdown
   * shows a "Retry" that re-publishes just this platform.
   */
  onRetry?: () => void
  /**
   * True while this platform's creative is mid-publish (retry-now or a sweep in
   * flight). The trailing slot shows a spinner and the failure dropdown is
   * suppressed — the row reads "publishing…" until Pusher lands the result.
   */
  isPublishing?: boolean
  /**
   * Post-attempt mode (the post is live, failed, or publishing — the platform
   * set can't change). The trailing slot collapses to STATUS DOTS: green =
   * published, red = failed (tap to expand the reason), nothing otherwise.
   * No selection badges, no connect hints — the row only reports outcomes.
   */
  readOnly?: boolean
  /** This platform's creative already published (drives the green dot). */
  isPublished?: boolean
}

/**
 * One row in PostModal's left column. Click anywhere on the row body to
 * make it the previewed platform; click the trailing +/✓ to toggle whether
 * it ships in the publish list. The two concerns are split deliberately
 * so a user can browse previews without committing each one to publish.
 *
 * Visual states:
 * - Previewing: acid left-bar + bg-3 fill.
 * - Selected for publish: full color icon + paper text + acid trailing ✓.
 * - Not connected: tiny `paper-faint` dot beside the +; the dot is a
 *   forward-looking cue that the Schedule CTA will run OAuth before
 *   publishing. Tapping + never triggers OAuth — that's deferred to submit.
 */
export function PostModalPlatformRow({
  platform,
  isPreviewing,
  isSelected,
  isConnected,
  isConnecting,
  onPreview,
  onToggleSelect,
  disabledReason,
  locked = false,
  failure,
  onRetry,
  isPublishing = false,
  readOnly = false,
  isPublished = false,
  isCustomized = false,
}: PostModalPlatformRowProps) {
  const [expanded, setExpanded] = useState(false)
  const label = platformLabel(platform)
  const isDisabled = !!disabledReason
  const failureReason = failure?.reason
  const isRestricted = failureReason === 'account_restricted'
  const hasFailure = !!failureReason
  // Non-restriction failures use the shared reason→copy map for the popover;
  // restriction keeps its bespoke title/body/external-link chrome below so the
  // pixel-identical treatment is preserved.
  const failureCopy =
    hasFailure && !isRestricted ? getPublishFailureCopy(failureReason, label) : null

  return (
    // When the failure dropdown is open, the WRAPPER carries the border/fill so
    // the row and its explanation read as one card — a separately-bordered box
    // under the row looked like a different surface.
    <div
      data-platform={platform}
      data-selected={isSelected || undefined}
      className={cn(
        'flex flex-col',
        hasFailure && expanded && 'rounded-[10px] border border-[color:var(--alert-error-border)]'
      )}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onPreview}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onPreview()
          }
        }}
        aria-pressed={isPreviewing}
        className={cn(
          'group relative flex w-full cursor-pointer items-center gap-3 rounded-[10px] px-2.5 py-2 text-left transition-colors duration-150 outline-none',
          isPreviewing ? 'bg-bg-3' : 'hover:bg-bg-3/60',
          'focus-visible:ring-2 focus-visible:ring-acid'
        )}
      >
        {/* Active-preview left bar — slim, sits inside the row's left padding. */}
        <span
          aria-hidden
          className={cn(
            'absolute top-2 bottom-2 left-0 w-[3px] rounded-r-full bg-acid transition-opacity duration-200',
            isPreviewing ? 'opacity-100' : 'opacity-0'
          )}
        />

        {/* Platform icon disc — leading. */}
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center transition-all duration-200',
            isSelected
              ? 'opacity-100 grayscale-0'
              : 'opacity-60 grayscale group-hover:opacity-90 group-hover:grayscale-0'
          )}
        >
          <PlatformIcon channel={platform} size={28} />
        </span>

        {/* Label */}
        <span
          className={cn(
            'flex-1 truncate text-[13px] font-medium tracking-tight transition-colors duration-200',
            isSelected ? 'text-paper' : 'text-paper-dim group-hover:text-paper'
          )}
        >
          {label}
        </span>

        {/* Customized marker — this platform carries its own hand-edited copy
          (it won't auto-update from the post). Mirrors the composer chip. */}
        {isCustomized && (
          <PenLine
            size={12}
            aria-label={`${label} is customized`}
            className="shrink-0 text-paper-faint"
          />
        )}

        {/* Connection hint — tiny dot beside the trailing badge whenever the
          platform requires OAuth and isn't connected, including when the row
          is selected (the auto-selected primary lands here). The dot is a
          forward-looking cue: clicking Schedule will trigger OAuth before
          the publish runs. */}
        {!isConnected && !isConnecting && !readOnly && !(locked && !isSelected) && (
          <span
            aria-label={`${label} not connected`}
            title={`Connecting required at publish time`}
            className="h-1.5 w-1.5 shrink-0 rounded-full bg-paper-faint"
          />
        )}

        {/* Trailing +/✓ badge — toggles publish selection only. Stops
          propagation so it doesn't double as a row click (which would
          switch preview). OAuth for unconnected platforms is deferred to
          the Schedule CTA in PostModal#handleSubmit. The spinner here
          reflects that submit-time OAuth, not a row-tap.
          When `locked`, the badge becomes a static read-only indicator:
          disabled, no hover affordances, no click handler. The composer
          entry path uses this — the post's platform set is fixed. */}
        {/* Trailing slot: mid-publish → spinner; read-only → status dots only
          (green = published, red = failed and toggles the reason); failed →
          ⚠️ toggle (opens the inline dropdown); locked + unselected → nothing
          (a locked post can't grow platforms, so a disabled "+" is a dead add
          affordance); otherwise the +/✓ selection toggle. Recovery is
          per-platform via the dropdown's Retry, not by re-selecting. */}
        {isPublishing ? (
          <span
            aria-label={`${label} publishing`}
            title={`Publishing to ${label}…`}
            className="flex h-5 w-5 shrink-0 items-center justify-center text-paper-dim"
          >
            <Loader2 size={14} className="animate-spin" />
          </span>
        ) : readOnly ? (
          isPublished ? (
            <span
              aria-label={`${label} published`}
              title={`Published to ${label}`}
              className="flex h-5 w-5 shrink-0 items-center justify-center"
            >
              <span className="h-2 w-2 rounded-full bg-acid" />
            </span>
          ) : hasFailure ? (
            // Failed row: inline ↻ (the one-tap recovery — the footer promises
            // "retry it from the platform list", so it can't hide behind an
            // expansion), then red dot + chevron (the dot is the status, the
            // chevron makes the reason dropdown discoverable).
            <span className="flex shrink-0 items-center gap-1.5">
              {onRetry && (
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation()
                    onRetry()
                  }}
                  onPointerDown={e => e.stopPropagation()}
                  aria-label={`Retry ${label}`}
                  title={`Retry ${label}`}
                  className="flex h-5 w-5 cursor-pointer items-center justify-center text-paper-dim transition-colors hover:text-paper"
                >
                  <RotateCcw size={13} />
                </button>
              )}
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  setExpanded(v => !v)
                }}
                onPointerDown={e => e.stopPropagation()}
                aria-expanded={expanded}
                aria-label={`${label} publish failed, show why`}
                title={`${label} publish failed`}
                className="flex shrink-0 cursor-pointer items-center gap-1 text-[color:var(--alert-error-text)]"
              >
                <span className="h-2 w-2 rounded-full bg-destructive" />
                <ChevronDown
                  size={14}
                  className={cn('transition-transform duration-200', expanded && 'rotate-180')}
                />
              </button>
            </span>
          ) : null
        ) : hasFailure ? (
          // Failed platform (restriction OR autopilot publish failure): a red
          // alert that toggles an inline dropdown UNDER the row with the reason +
          // fix (+ Retry for marker failures). Chevron rotates when open.
          <button
            type="button"
            onClick={e => {
              e.stopPropagation()
              setExpanded(v => !v)
            }}
            onPointerDown={e => e.stopPropagation()}
            aria-expanded={expanded}
            aria-label={isRestricted ? `${label} blocked posting` : `${label} publish failed`}
            title={isRestricted ? `${label} blocked posting` : `${label} publish failed`}
            className="flex shrink-0 items-center gap-0.5 text-[color:var(--alert-error-text)]"
          >
            <AlertTriangle size={15} />
            <ChevronDown
              size={14}
              className={cn('transition-transform duration-200', expanded && 'rotate-180')}
            />
          </button>
        ) : locked && !isSelected ? null : (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation()
              if (isConnecting || isDisabled || locked) return
              onToggleSelect()
            }}
            title={
              locked
                ? `${label} is fixed by the post's platform set`
                : isDisabled
                  ? disabledReason
                  : isConnecting
                    ? `Connecting ${label}…`
                    : isSelected
                      ? `Remove ${label} from publish list`
                      : `Add ${label} to publish list`
            }
            aria-pressed={isSelected}
            disabled={isConnecting || isDisabled || locked}
            className={cn(
              'relative flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full transition-all duration-200',
              !locked && 'active:scale-90',
              isSelected
                ? cn('text-ink-on-acid bg-acid', !locked && 'hover:opacity-70')
                : cn(
                    'bg-bg-4 text-paper-dim',
                    !locked &&
                      'hover:scale-125 hover:bg-[var(--acid)] hover:text-[var(--ink-on-acid)] hover:shadow-[0_0_8px_var(--acid-soft)]'
                  ),
              isConnecting && 'cursor-progress opacity-80',
              isDisabled &&
                'cursor-not-allowed opacity-40 hover:scale-100 hover:bg-bg-4 hover:text-paper-dim hover:shadow-none',
              locked && 'cursor-default'
            )}
          >
            {isConnecting ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <>
                <Plus
                  size={12}
                  className={cn(
                    'absolute transition-all duration-200',
                    isSelected ? 'scale-75 rotate-90 opacity-0' : 'scale-100 rotate-0 opacity-100'
                  )}
                />
                <Check
                  size={12}
                  strokeWidth={3}
                  className={cn(
                    'absolute transition-all duration-200',
                    isSelected ? 'scale-100 rotate-0 opacity-100' : 'scale-75 -rotate-90 opacity-0'
                  )}
                />
              </>
            )}
          </button>
        )}
      </div>

      {/* Inline expansion UNDER the row — the failure reason + fix, and (for a
          marker failure) a per-platform Retry. Restriction keeps its bespoke
          external unlock chrome. No border of its own: the wrapper's single
          border makes row + explanation one card. */}
      {hasFailure && expanded && (
        <div
          className="flex flex-col gap-2 px-2.5 pt-0.5 pb-2.5"
          onClick={e => e.stopPropagation()}
        >
          {isRestricted ? (
            <>
              <p className="text-[13px] font-medium text-[color:var(--alert-error-text)]">
                {label} blocked posting
              </p>
              <p className="text-[12px] text-paper-dim">
                {label} restricted this account, so this post won't publish there. Sort it out with{' '}
                {label}, then reset the connection on the Integrations page to resume.
              </p>
              <div className="flex flex-col gap-1.5">
                <a
                  href={PLATFORM_HOME_URL[platform] ?? 'https://www.instagram.com/'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[12px] font-medium text-[color:var(--alert-error-text)] underline underline-offset-2"
                >
                  Open {label}
                  <ExternalLink size={12} />
                </a>
                <a
                  href="/integrations"
                  className="inline-flex items-center gap-1 text-[12px] font-medium text-paper-dim underline underline-offset-2 hover:text-paper"
                >
                  Go to Integrations to reset
                </a>
              </div>
            </>
          ) : (
            <>
              {failureCopy && <PublishFailureCopyBlock copy={failureCopy} />}
              {/* In read-only mode Retry lives inline next to the status dot —
                  the dropdown only explains and links the fix. */}
              {onRetry && !readOnly && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="mt-0.5 inline-flex shrink-0 items-center gap-1 self-start text-[12px] font-medium text-paper transition-colors hover:text-acid"
                >
                  <RotateCcw size={12} />
                  Retry
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
